import { findTranslationKeys } from '@/lib/utils/translationKeyRecorder';
import type { InspectedTarget } from './types';

/** Marks the inspector's own UI, which is never inspected or blocked. */
export const INSPECTOR_UI_ATTRIBUTE = 'data-translation-inspector';

const TEXT_ATTRIBUTES = ['placeholder', 'aria-label', 'title', 'alt'];

/** An icon inside a button is hovered as often as the button's label. */
const MAX_ANCESTORS = 4;

export function isInspectorUi(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest(`[${INSPECTOR_UI_ATTRIBUTE}]`) !== null
  );
}

/**
 * The element's own text: its direct text nodes and text attributes. Not its
 * descendants', or every container would match whatever it holds.
 */
function ownTexts(element: Element): string[] {
  const nodes = [...element.childNodes]
    .filter(node => node.nodeType === Node.TEXT_NODE)
    .map(node => node.textContent ?? '');
  const attributes = TEXT_ATTRIBUTES.map(
    name => element.getAttribute(name) ?? '',
  );
  return [nodes.join(''), ...nodes, ...attributes]
    .map(text => text.trim())
    .filter(Boolean);
}

/** The nearest element, from `target` up, whose own text is translated. */
export function inspectElement(
  target: EventTarget | null,
): InspectedTarget | null {
  if (!(target instanceof Element) || isInspectorUi(target)) return null;
  let element: Element | null = target;
  for (let depth = 0; element && depth < MAX_ANCESTORS; depth += 1) {
    const keys = findTranslationKeys(ownTexts(element));
    if (keys.length > 0) {
      const { top, left, width, height } = element.getBoundingClientRect();
      return { rect: { top, left, width, height }, keys };
    }
    element = element.parentElement;
  }
  return null;
}
